/**
 * checkboxVisibility.js
 * Diagnostic plugin that verifies checkbox inputs and .dt-checkbox-wrapper visuals
 * maintain valid visibility and opacity across default and custom-rendered modes.
 */
export const checkboxVisibilityPlugin = {
    id: 'checkbox-visibility',
    name: 'Checkbox Visibility & Opacity Regression Detector',
    category: 'regression',
    description: 'Detects blanket CSS opacity rules or styling regressions that hide standard or custom checkbox inputs.',

    async run(context) {
        const checks = [];
        const errors = [];
        let passed = true;

        // 1. Inspect loaded stylesheets for blanket `table.dt-core input[type=checkbox] { opacity: 0 }` rules
        const blanketRules = context.inspectCssRules(
            /table\.dt-core\s+input\[type=["']?checkbox["']?\]/i,
            /opacity:\s*0/i
        );

        if (blanketRules.length > 0) {
            passed = false;
            const msg = `Found ${blanketRules.length} blanket opacity:0 CSS rule(s) targeting table checkboxes.`;
            errors.push(msg);
            checks.push({ target: 'Stylesheet Rules', passed: false, message: msg });
        } else {
            checks.push({ target: 'Stylesheet Rules', passed: true, message: 'No blanket checkbox opacity:0 rules found.' });
        }

        // 2. Mount default checkbox scenario
        const result1 = await context.createTable(builder => {
            builder
                .withPaging(5)
                .withColumn(c => c.withCheckbox())
                .withColumn(c => c.withData('Name').withTitle('Name'))
                .withRows([{ Name: 'Alpha' }, { Name: 'Beta' }]);
        });

        if (!result1.skipped) {
            const { tableEl: table1, destroy: destroy1 } = result1;
            const wrapper = table1.querySelector('.dt-checkbox-wrapper');
            const defaultCheck = {
                target: 'Default Checkbox Wrapper',
                passed: Boolean(wrapper),
                message: wrapper ? 'Default .dt-checkbox-wrapper element rendered.' : 'Missing .dt-checkbox-wrapper.'
            };
            checks.push(defaultCheck);
            if (!defaultCheck.passed) {
                passed = false;
                errors.push(defaultCheck.message);
            }
            destroy1();
        } else {
            checks.push({ target: 'Default Checkbox Wrapper', passed: true, message: `Skipped: ${result1.skipReason}` });
        }

        // 3. Mount custom-rendered checkbox scenario
        const result2 = await context.createTable(builder => {
            builder
                .withPaging(5)
                .withColumn(c => c.withCheckbox().withRender((val, row) => `<input type="checkbox" class="diag-custom-cb" data-id="${row.Name}" />`))
                .withColumn(c => c.withData('Name').withTitle('Name'))
                .withRows([{ Name: 'Alpha' }, { Name: 'Beta' }]);
        });

        if (!result2.skipped) {
            const { tableEl: table2, destroy: destroy2 } = result2;
            const customCb = table2.querySelector('input.diag-custom-cb');
            const opacity = customCb ? parseFloat(window.getComputedStyle(customCb).opacity) : -1;
            const customCheck = {
                target: 'Custom Checkbox Render',
                passed: opacity > 0,
                message: opacity > 0
                    ? `Custom checkbox visible with computed opacity: ${opacity}.`
                    : `Custom checkbox hidden or has opacity <= 0 (${opacity}).`
            };
            checks.push(customCheck);
            if (!customCheck.passed) {
                passed = false;
                errors.push(customCheck.message);
            }
            destroy2();
        } else {
            checks.push({ target: 'Custom Checkbox Render', passed: true, message: `Skipped: ${result2.skipReason}` });
        }

        return {
            passed,
            checks,
            errors,
            details: passed
                ? 'All checkbox rendering paths maintain valid visibility.'
                : `${errors.length} checkbox visibility check(s) failed.`
        };
    }
};
